// ============================================================================
// ui/busy-dots.js — the animated ellipsis
// ============================================================================
// Ryan 2026-07-29: *"should the '...' animate to show '.' then '..' then '...' then back to '.'
// sort of thing?"*
//
// Yes, and for a reason beyond decoration: a STATIC ellipsis is indistinguishable from a frozen UI.
// It is the one glyph in the copy that claims something is still happening, so it should be the one
// glyph that proves it.
//
// Built as three real elements rather than an animated `content` string. Animating `content` works
// but re-lays-out the text on every step, so the label's width jitters and anything centred beside it
// shuffles. Three fixed dots reserve their space once and only change OPACITY — no reflow, and the
// label never moves.
//
// The dots are `aria-hidden`: the message text is announced through the live region, and "period
// period period" is noise. Under `prefers-reduced-motion` they hold steady at full opacity, so the
// ellipsis still reads as an ellipsis without moving.

import { h } from './dom.js';

/** The trailing ellipsis this recognises — a single U+2026, which is what all the copy uses. */
const ELLIPSIS = '\u2026';

/** Three cycling dots. Standalone, in case a caller wants them without a label. */
export function busyDots() {
  return h('span', { class: 'wd-dots', 'aria-hidden': 'true' },
    h('i', {}), h('i', {}), h('i', {}));
}

/**
 * A label whose trailing "…" becomes animated dots. Returns a DocumentFragment so it drops straight
 * into `replaceChildren`.
 *
 * Text WITHOUT a trailing ellipsis comes back untouched — so "Cleaned it up ✓" and "Found 3 colors"
 * never sprout dots, and a caller can pass anything without checking first.
 */
export function labelWithDots(text) {
  const s = String(text == null ? '' : text);
  const frag = document.createDocumentFragment();
  if (!s.endsWith(ELLIPSIS)) { frag.appendChild(document.createTextNode(s)); return frag; }
  frag.appendChild(document.createTextNode(s.slice(0, -ELLIPSIS.length)));
  frag.appendChild(busyDots());
  return frag;
}
